import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

const HiringProcessContext = createContext();

export const useHiringProcess = () => useContext(HiringProcessContext);

export const HiringProcessProvider = ({ children }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [jobTitle, setJobTitle] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [processedJd, setProcessedJd] = useState('');
  const [createdJdId, setCreatedJdId] = useState(null);
  const [createdJd, setCreatedJd] = useState(null);
  const [teamEmails, setTeamEmails] = useState(['']);
  const [emailErrors, setEmailErrors] = useState(['']);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [simulationLaunched, setSimulationLaunched] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!createdJdId) return;
    const unsubscribe = onSnapshot(doc(db, 'jobDescriptions', createdJdId), (doc) => {
      if (doc.exists()) {
        setCreatedJd(doc.data());
      }
    });
    return () => unsubscribe();
  }, [createdJdId]);

  const validateEmail = (email) => {
    if (!email) return ''; // No error if empty, as it's optional
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase()) ? '' : 'Invalid email address';
  };

  const handleTeamEmailChange = (index, value) => {
    const newEmails = [...teamEmails];
    newEmails[index] = value;
    setTeamEmails(newEmails);
    const newErrors = [...emailErrors];
    newErrors[index] = validateEmail(value);
    setEmailErrors(newErrors);
  };

  const addTeamMember = () => {
    if (teamEmails.length < 3) {
      setTeamEmails([...teamEmails, '']);
      setEmailErrors([...emailErrors, '']);
    }
  };

  const removeTeamMember = (index) => {
    const newEmails = teamEmails.filter((_, i) => i !== index);
    setTeamEmails(newEmails);
    const newErrors = emailErrors.filter((_, i) => i !== index);
    setEmailErrors(newErrors);
  };

  const handleProcessJd = async () => {
    if (!jdFile) return;
    setIsProcessing(true);
    setNotification({ message: '', type: '' });
    try {
      const triggerUrl = process.env.REACT_APP_SIMULATION_TRIGGER_URL;
      if (!triggerUrl) throw new Error("Simulation trigger URL is not configured.");
      const formData = new FormData();
      formData.append('jd-file', jdFile);
      const response = await fetch(`${triggerUrl}/process`, { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Processing failed.');
      setProcessedJd(JSON.stringify(result.processed_jd, null, 2));
      setStep(2);
    } catch (err) {
      setNotification({ message: err.message, type: 'error' });
    }
    finally {
      setIsProcessing(false);
    }
  };

  const handleAssignTeam = async () => {
    if (!processedJd) return;
    if (emailErrors.some(e => e)) {
      setNotification({ message: 'Please fix the errors in the email fields.', type: 'error' });
      return;
    }

    setIsProcessing(true);
    setNotification({ message: '', type: '' });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Authentication error.");
      const idToken = await user.getIdToken();

      const resolvedMembers = await Promise.all(
        teamEmails
          .filter(email => email.trim() !== '')
          .map(async email => {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/findUserByEmail`, { 
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ email: email.trim() }),
            });
            if (!res.ok) throw new Error(`Could not find user for email: ${email}`);
            const result = await res.json();
            return { uid: result.uid, email: email.trim() };
          })
      );

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/createJobDescription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          jobDescription: JSON.parse(processedJd),
          teamMembers: resolvedMembers,
        }),
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to create Job Description.");
      }
      
      const result = await response.json();
      setCreatedJdId(result.jdId);
      setStep(3);
    } catch (err) {
      setNotification({ message: err.message, type: 'error' });
    }
    finally {
      setIsProcessing(false);
    }
  };

  const handleLaunchSimulation = async () => {
    if (!processedJd) {
      setNotification({ message: "Cannot launch without processed JD content.", type: 'error' });
      return;
    }
    setIsLaunching(true);
    setNotification({ message: '', type: '' });
    try {
      const triggerUrl = process.env.REACT_APP_SIMULATION_TRIGGER_URL;
      const orchestratorUrlSuffix = process.env.REACT_APP_ORCHESTRATOR_URL_SUFFIX;

      if (!triggerUrl || !orchestratorUrlSuffix) {
        throw new Error("Application is not configured correctly. Missing trigger or orchestrator URL suffix.");
      }
      const orchestratorUrl = `https://pod-plus-orchestrator${orchestratorUrlSuffix}`;

      const payload = {
        orchestrator_url: orchestratorUrl,
        sim_mode: 'search',
        jd_id: createdJdId,
      };

      const response = await fetch(`${triggerUrl}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to launch simulation.');
      }

      setSimulationLaunched(true);
      setNotification({ message: t('startHiring.launchSuccessAlert', { jdId: result.jd_id }), type: 'success' });

    } catch (err) {
      setNotification({ message: err.message, type: 'error' });
    }
    finally {
      setIsLaunching(false);
    }
  };

  const allTeamMembersProvidedInput = () => {
    if (!createdJd) {
      return false;
    }
    if (!createdJd.teamMembers || createdJd.teamMembers.length === 0) {
      return true;
    }
    return createdJd.teamMembers.every(member => member.status === 'input_provided');
  };

  const value = {
    step, setStep,
    jobTitle, setJobTitle,
    jdFile, setJdFile,
    processedJd, setProcessedJd,
    createdJdId, setCreatedJdId,
    createdJd,
    teamEmails, setTeamEmails,
    emailErrors, setEmailErrors,
    isProcessing, setIsProcessing,
    isLaunching, setIsLaunching,
    notification, setNotification,
    simulationLaunched, setSimulationLaunched,
    fileInputRef,
    validateEmail,
    handleTeamEmailChange,
    addTeamMember,
    removeTeamMember,
    handleProcessJd,
    handleAssignTeam,
    handleLaunchSimulation,
    allTeamMembersProvidedInput
  };

  return (
    <HiringProcessContext.Provider value={value}>
      {children}
    </HiringProcessContext.Provider>
  );
};